/**
 * The driver's in-progress ride offer.
 *
 * Same rationale as the passenger's search draft: the form spans several panels
 * and survives navigation between them, and none of it exists on the server
 * until it is published.
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { Minor, Point, RideType, UUID } from "../../api/types";
import { DEFAULT_CURRENCY } from "../../lib/money";

export interface RideDraft {
    carId: UUID | null;
    startAddress: string;
    endAddress: string;
    origin: Point | null;
    destination: Point | null;
    /** RFC3339. */
    departureAt: string;
    seats: number;
    /** Minor units. Null until the driver prices the ride. */
    farePerSeat: Minor | null;
    currency: string;
    rideType: RideType;
    childOnBoard: boolean;
}

export const emptyRideDraft: RideDraft = {
    carId: null,
    startAddress: "",
    endAddress: "",
    origin: null,
    destination: null,
    departureAt: "",
    seats: 1,
    farePerSeat: null,
    currency: DEFAULT_CURRENCY,
    rideType: "shared",
    childOnBoard: false,
};

const rideDraftSlice = createSlice({
    name: "rideDraft",
    initialState: emptyRideDraft,
    reducers: {
        rideDraftUpdated(state, action: PayloadAction<Partial<RideDraft>>) {
            return { ...state, ...action.payload };
        },
        rideDraftCleared() {
            return emptyRideDraft;
        },
    },
});

export const { rideDraftUpdated, rideDraftCleared } = rideDraftSlice.actions;

export default rideDraftSlice.reducer;
